import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useAuthResetPasswordCreateMutation } from '../../services/GLOWClient';
import { theme } from '../../theme';
import FormInput from '../../SystemDesign/formInput';
import { useTranslation } from 'react-i18next';

const ResetPassword = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const langParam = searchParams.get('lang');

  useEffect(() => {
    console.log('langParam:', langParam);
    if (langParam && ['fr', 'en', 'ar'].includes(langParam)) {
      console.log('Changing language to:', langParam);
      i18n.changeLanguage(langParam);
    }
  }, [langParam]);

  // Validation schema with translations
  const resetPasswordSchema = Yup.object().shape({
    password: Yup.string()
      .required(t('reset_password.error.password_required'))
      .min(8, t('reset_password.error.password_min')),
    confirm_password: Yup.string()
      .required(t('reset_password.error.confirm_required'))
      .oneOf([Yup.ref('password')], t('reset_password.error.passwords_mismatch'))
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { token } = useParams();
  const [resetPassword, { isLoading }] = useAuthResetPasswordCreateMutation();
  const [tokenValid, setTokenValid] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await resetPassword({
        token,
        resetPasswordRequest: {
          password: values.password,
          confirm_password: values.confirm_password
        }
      }).unwrap();
      setSuccess(true);
      setError('');
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error('Password reset failed:', err);
      if (err.status === 404) {
        setTokenValid(false);
      }
      // Display backend error message (already translated)
      const errorMessage = err.data?.token || err.data?.password || err.data?.detail || err.data?.error || t('reset_password.error.generic');
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const customStyles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: 3,
      backgroundColor: theme.colors.background.primary
    },
    paper: {
      padding: 4,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      maxWidth: 500,
      width: '100%',
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
      borderRadius: 2
    },
    title: {
      marginBottom: 3,
      color: theme.colors.primary[500],
      fontWeight: 600
    },
    subtitle: {
      marginBottom: 4,
      textAlign: 'center',
      color: theme.colors.text.secondary
    },
    form: {
      width: '100%',
      marginTop: 2
    },
    textField: {
      marginBottom: 3
    },
    submitButton: {
      marginTop: 2,
      marginBottom: 2,
      padding: '12px 0',
      backgroundColor: theme.colors.primary[500],
      '&:hover': {
        backgroundColor: theme.colors.primary[600]
      }
    },
    backButton: {
      marginTop: 2,
      color: theme.colors.text.secondary,
      '&:hover': {
        backgroundColor: 'transparent',
        color: theme.colors.primary[500]
      }
    },
    successMessage: {
      marginBottom: 3,
      width: '100%'
    }
  };

  return (
    <Container component="main" maxWidth="xs" sx={customStyles.container}>
      <Paper elevation={3} sx={customStyles.paper}>
        <Typography component="h1" variant="h4" sx={customStyles.title}>
          {t('reset_password.title')}
        </Typography>
        
        {!tokenValid ? (
          <Box sx={{ width: '100%' }}>
            <Alert severity="error" sx={customStyles.successMessage}>
              {error}
            </Alert>
            <Button
              fullWidth
              variant="contained"
              sx={customStyles.submitButton}
              onClick={() => navigate('/')}
            >
              {t('reset_password.back_to_login')}
            </Button>
          </Box>
        ) : success ? (
          <Box sx={{ width: '100%' }}>
            <Alert severity="success" sx={customStyles.successMessage}>
              {t('reset_password.success_message')}
            </Alert>
            <CircularProgress size={24} sx={{ display: 'block', margin: '0 auto' }} />
          </Box>
        ) : (
          <>
            <Typography variant="body1" sx={customStyles.subtitle}>
              {t('reset_password.subtitle')}
            </Typography>

            <Formik
              initialValues={{ password: '', confirm_password: '' }}
              validationSchema={resetPasswordSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form style={{ width: '100%' }}>
                  {error && (
                    <Alert severity="error" sx={{ marginBottom: 3 }}>
                      {error}
                    </Alert>
                  )}
                  
                  <FormInput
                    name="password"
                    label={t('reset_password.new_password')}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder={t('reset_password.placeholder_password')}
                    sx={customStyles.textField}
                    icon={<LockIcon color="action" />}
                    onIconClick={togglePasswordVisibility}
                    iconIsButton={true}
                  />
                  
                  <FormInput
                    name="confirm_password"
                    label={t('reset_password.confirm_password')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder={t('reset_password.placeholder_confirm')}
                    sx={customStyles.textField}
                    icon={<LockIcon color="action" />}
                    onIconClick={toggleConfirmPasswordVisibility}
                    iconIsButton={true}
                  />
                  
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isSubmitting}
                    sx={customStyles.submitButton}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      t('reset_password.submit')
                    )}
                  </Button>
                  
                  <Button
                    onClick={() => navigate('/')}
                    fullWidth
                    startIcon={<ArrowBackIcon />}
                    sx={customStyles.backButton}
                  >
                    {t('reset_password.back_to_login')}
                  </Button>
                </Form>
              )}
            </Formik>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ResetPassword;
